import { TeamPermission, useAuthenticationContext } from 'nekst-api';
import { ToggleWrapper } from './ToggleWrapper';
import React from 'react';
import { useAuthorizationChecker } from 'authorization-scope';

export enum ToggledFeature {
  AILAUNCHER = 'AILAUNCHER',
  TEAM_MEMBERS_MANAGEMENT = 'TEAM_MEMBERS_MANAGEMENT',
  TEAM_MEMBERS_MANAGEMENT_SEATS = 'TEAM_MEMBERS_MANAGEMENT_SEATS',
  TASKS_GROUPS = 'TASKS_GROUPS',
  LAUNCHING_FROM_CONTRACT = 'LAUNCHING_FROM_CONTRACT',
  NEW_UI = 'NEW_UI',
  DETAILS_FIELD_SETS = 'DETAILS_FIELD_SETS',
  BUSINESS_DAYS = 'BUSINESS_DAYS',
  EVENT_TASKS = 'EVENT_TASKS',
  COPY_PLAN_TASKS = 'COPY_PLAN_TASKS',
  SEND_DOCUMENTS_AS_ATTACHMENTS = 'SEND_DOCUMENTS_AS_ATTACHMENTS',
  FUB_CONTACTS_INTEGRATION = 'FUB_CONTACTS_INTEGRATION',
  FUB_DEALS_INTEGRATION = 'FUB_DEALS_INTEGRATION',
  NEW_PRICE = 'NEW_PRICE',
  NEW_TRANSACTIONS_MANAGEMENT = 'NEW_TRANSACTIONS_MANAGEMENT',
  REQUEST_MARKETS = 'REQUEST_MARKETS',
  CUSTOM_MARKETS = 'CUSTOM_MARKETS',
  SETUP_AREAS_VERSIONING = 'SETUP_AREAS_VERSIONING',
  GOOGLE_SIGNIN = 'GOOGLE_SIGNIN',
  APPLE_SIGNIN = 'APPLE_SIGNIN',
  GMAIL_INTEGRATION = 'GMAIL_INTEGRATION',
  OUTLOOK_INTEGRATION = 'OUTLOOK_INTEGRATION',
  CUSTOMIZABLE_COMMISSION_FIELD = 'CUSTOMIZABLE_COMMISSION_FIELD',
  CUSTOMIZABLE_CLOSING_DATE_FIELD = 'CUSTOMIZABLE_CLOSING_DATE_FIELD',
  MANAGE_COMMISSION_AND_CLOSING_DATES = 'MANAGE_COMMISSION_AND_CLOSING_DATES',
  CREATE_NOTE_FROM_TRANSACTION_PAGE = 'CREATE_NOTE_FROM_TRANSACTION_PAGE',
  PLAN_EVENT_TASK = 'PLAN_EVENT_TASK',
  SEND_DOCUMENTS_VIA_EMAIL = 'SEND_DOCUMENTS_VIA_EMAIL',
  EVENT_TASKS_PREVIEW = 'EVENT_TASKS_PREVIEW',
  ROADMAP_CHECKBOXES = 'ROADMAP_CHECKBOXES',
  MARKET_TIMEZONE = 'MARKET_TIMEZONE',
  CLIENT_PORTAL_BRANDING = 'CLIENT_PORTAL_BRANDING',
  ROADMAP_VIDEO_URL = 'ROADMAP_VIDEO_URL',
  PREFERENCES_PAGE = 'PREFERENCES_PAGE',
  BULK_CONTACTS_IMPORT = 'BULK_CONTACTS_IMPORT',
  CLIENT_APP = 'CLIENT_APP',
  HTML_SIGNATURE = 'HTML_SIGNATURE',
  SCRATCHPAD = 'SCRATCHPAD',
  LLM_CHAT = 'LLM_CHAT',
  NEW_WYSIWYG_STYLES = 'NEW_WYSIWYG_STYLES',
  NEW_WYSIWYG_STYLES_CONFIGURATION = 'NEW_WYSIWYG_STYLES_CONFIGURATION',
  LOCK_IN_BANNER = 'LOCK_IN_BANNER',
  AUTO_ASSIGN_ROLES = 'AUTO_ASSIGN_ROLES',
  MULTI_WORKFLOW = 'MULTI_WORKFLOW',
  EMAIL_DESCRIPTION_TOOL = 'EMAIL_DESCRIPTION_TOOL',
  APPLY_ADDITIONAL_WORKFLOW = 'APPLY_ADDITIONAL_WORKFLOW',
}

export default function useFeatureToggleService() {
  const authContext = useAuthenticationContext();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const teamId = authContext?.user.teamId;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const email = authContext?.user.profile.primaryEmail || '';

  const { isGranted } = useAuthorizationChecker();

  const isForDefaultTeams = [
    1,
    2,
    697,
    18370 // Steeve
  ].includes(teamId || 0);

  const isHtmlSignatureUser = teamId === 260502;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isForDeveloperOnly = teamId === 697;

  const isTeamMembersManagementEnabled = () => {
    return isGranted(TeamPermission.FEATURE_TEAM_MEMBERS_MANAGEMENT);
  };

  const isTeamMembersManagementSeatsEnabled = () => {
    return isGranted(TeamPermission.FEATURE_TEAM_MEMBERS_SEATS_MANAGEMENT);
  };

  const isTasksGroupsEnabled = () => {
    return true;
  };

  const isDetailsFieldSetsEnabled = () => {
    return isGranted(TeamPermission.FEATURE_DETAILS_FIELDS_SETS);
  };

  const isLaunchingFromContractEnabled = () => {
    return isGranted(TeamPermission.FEATURE_LAUNCHING_TRANSACTION_FROM_CONTRACT);
  };

  const isNewUiEnabled = () => {
    return true;
  };

  const isNewSmartTagsEnabled = () => {
    return isGranted(TeamPermission.FEATURE_NEW_TAGS);
  };

  const isBusinessDaysEnabled = () => {
    return isGranted(TeamPermission.FEATURE_BUSINESS_DAYS);
  };

  const isEventTasksEnabled = () => {
    return isGranted(TeamPermission.FEATURE_EVENT_TASK);
  };

  const isCopyPlanTasksEnabled = () => {
    return true;
  };

  const isSendDocumentsAsAttachmentsEnabled = () => {
    return true;
  };

  const isFubIntegrationEnabled = () => {
    return true;
  };

  const isFubDealsIntegrationEnabled = () => {
    return true;
  };

  const isNewPriceEnabled = () => {
    return true;
  };

  const isNewTransactionsManagementEnabled = () => {
    return true;
  };

  const isRequestMarketsEnabled = () => {
    return true;
  }

  const isCustomMarketsEnabled = () => {
    return true;
  }

  const isSetupAreasVersioningEnabled = () => {
    return true;
  }

  const isGoogleSigninEnabled = () => {
    return true;
  }

  const isAppleSigninEnabled = () => {
    return isForDeveloperOnly;
  }

  const isGmailIntegrationEnabled = () => {
    return true;
  }

  const isOutlookIntegrationEnabled = () => {
    return true;
  }

  const isCommissionFieldCustomizable = () => {
    return true;
  }

  const isClosingDateFieldCustomizable = () => {
    return true;
  }

  const isManageCommissionAndClosingDatesEnabled = () => {
    return true;
  }

  const isCreateNoteFromTransactionPageEnabled = () => {
    return true;
  }

  const isPlanEventTasksEnabled = () => {
    return true;
  }

  const isSendDocumentsViaEmailEnabled = () => {
    return true;
  }

  const isEventTaskPreviewEnabled = () => {
    return true;
  }

  const isRoadmapCheckboxesEnabled = () => {
    return true;
  }

  const isMarketTimezoneEnabled = () => {
    return true;
  }

  const isClientPortalBrandingEnabled = () => {
    return true;
  }

  const isRoadmapVideoUrlEnabled = () => {
    return true;
  }

  const isPreferencesPageEnabled = () => {
    return true;
  }

  const isBulkContactsImportEnabled = () => {
    return true;
  }

  const isClientAppEnabled = () => {
    return true;
  }

  const isHtmlSignatureEnabled = () => {
    return isHtmlSignatureUser || isForDeveloperOnly;
  }

  const isScratchpadEnabled = () => {
    return true;
  }

  const isNewWysiwygStylesEnabled = () => {
    return isGranted(TeamPermission.FEATURE_NEW_WYSIWYG_STYLES);
  }

  const isNewWysiwygStylesConfigurationEnabled = () => {
    return isGranted(TeamPermission.FEATURE_NEW_WYSIWYG_STYLES_CONFIGURATION);
  }

  const isLockInBannerEnabled = () => {
    return true;
  }

  const isAutoAssignRolesEnabled = () => {
    return true;
  }

  const isMultiWorkflowEnabled = () => {
    return isForDefaultTeams;
  }

  const isEmailDescriptionToolEnabled = () => {
    return isForDefaultTeams;
  }

  const isApplyAdditionalWorkflowEnabled = () => {
    return isForDeveloperOnly;
  }

  const isLlmChatEnabled = () => {
    return true;
  }

  const isFeatureEnabled = (feature: ToggledFeature) => {
    switch (feature) {
      case ToggledFeature.AILAUNCHER:
        return isLaunchingFromContractEnabled();
      case ToggledFeature.TEAM_MEMBERS_MANAGEMENT:
        return isTeamMembersManagementEnabled();
      case ToggledFeature.TEAM_MEMBERS_MANAGEMENT_SEATS:
        return isTeamMembersManagementSeatsEnabled();
      case ToggledFeature.TASKS_GROUPS:
        return isTasksGroupsEnabled();
      case ToggledFeature.LAUNCHING_FROM_CONTRACT:
        return isLaunchingFromContractEnabled();
      case ToggledFeature.NEW_UI:
        return isNewUiEnabled();
      case ToggledFeature.DETAILS_FIELD_SETS:
        return isDetailsFieldSetsEnabled();
      case ToggledFeature.BUSINESS_DAYS:
        return isBusinessDaysEnabled();
      case ToggledFeature.EVENT_TASKS:
        return isEventTasksEnabled();
      case ToggledFeature.COPY_PLAN_TASKS:
        return isCopyPlanTasksEnabled();
      case ToggledFeature.SEND_DOCUMENTS_AS_ATTACHMENTS:
        return isSendDocumentsAsAttachmentsEnabled();
      case ToggledFeature.FUB_CONTACTS_INTEGRATION:
        return isFubIntegrationEnabled();
      case ToggledFeature.NEW_PRICE:
        return isNewPriceEnabled();
      case ToggledFeature.FUB_DEALS_INTEGRATION:
        return isFubDealsIntegrationEnabled();
      case ToggledFeature.NEW_TRANSACTIONS_MANAGEMENT:
        return isNewTransactionsManagementEnabled();
      case ToggledFeature.REQUEST_MARKETS:
        return isRequestMarketsEnabled();
      case ToggledFeature.CUSTOM_MARKETS:
        return isCustomMarketsEnabled();
      case ToggledFeature.SETUP_AREAS_VERSIONING:
        return isSetupAreasVersioningEnabled();
      case ToggledFeature.GOOGLE_SIGNIN:
        return isGoogleSigninEnabled();
      case ToggledFeature.APPLE_SIGNIN:
        return isAppleSigninEnabled();
      case ToggledFeature.GMAIL_INTEGRATION:
        return isGmailIntegrationEnabled();
      case ToggledFeature.OUTLOOK_INTEGRATION:
        return isOutlookIntegrationEnabled();
      case ToggledFeature.CUSTOMIZABLE_COMMISSION_FIELD:
        return isCommissionFieldCustomizable();
      case ToggledFeature.CUSTOMIZABLE_CLOSING_DATE_FIELD:
        return isClosingDateFieldCustomizable();
      case ToggledFeature.MANAGE_COMMISSION_AND_CLOSING_DATES:
        return isManageCommissionAndClosingDatesEnabled();
      case ToggledFeature.CREATE_NOTE_FROM_TRANSACTION_PAGE:
        return isCreateNoteFromTransactionPageEnabled();
      case ToggledFeature.PLAN_EVENT_TASK:
        return isPlanEventTasksEnabled();
      case ToggledFeature.SEND_DOCUMENTS_VIA_EMAIL:
        return isSendDocumentsViaEmailEnabled();
      case ToggledFeature.EVENT_TASKS_PREVIEW:
        return isEventTaskPreviewEnabled();
      case ToggledFeature.ROADMAP_CHECKBOXES:
        return isRoadmapCheckboxesEnabled();
      case ToggledFeature.MARKET_TIMEZONE:
        return isMarketTimezoneEnabled();
      case ToggledFeature.CLIENT_PORTAL_BRANDING:
        return isClientPortalBrandingEnabled();
      case ToggledFeature.ROADMAP_VIDEO_URL:
        return isRoadmapVideoUrlEnabled();
      case ToggledFeature.PREFERENCES_PAGE:
        return isPreferencesPageEnabled();
      case ToggledFeature.BULK_CONTACTS_IMPORT:
        return isBulkContactsImportEnabled();
      case ToggledFeature.CLIENT_APP:
        return isClientAppEnabled();
      case ToggledFeature.HTML_SIGNATURE:
        return isHtmlSignatureEnabled();
      case ToggledFeature.SCRATCHPAD:
        return isScratchpadEnabled();
      case ToggledFeature.LLM_CHAT:
        return isLlmChatEnabled();
      case ToggledFeature.NEW_WYSIWYG_STYLES:
        return isNewWysiwygStylesEnabled();
      case ToggledFeature.NEW_WYSIWYG_STYLES_CONFIGURATION:
        return isNewWysiwygStylesConfigurationEnabled();
      case ToggledFeature.LOCK_IN_BANNER:
        return isLockInBannerEnabled();
      case ToggledFeature.AUTO_ASSIGN_ROLES:
        return isAutoAssignRolesEnabled();
      case ToggledFeature.MULTI_WORKFLOW:
        return isMultiWorkflowEnabled();
      case ToggledFeature.EMAIL_DESCRIPTION_TOOL:
        return isEmailDescriptionToolEnabled();
      case ToggledFeature.APPLY_ADDITIONAL_WORKFLOW:
        return isApplyAdditionalWorkflowEnabled();
      default:
        return false;
    }
  };

  const isInDevelopment = (feature: ToggledFeature) => {
    switch (feature) {
      case ToggledFeature.FUB_DEALS_INTEGRATION:
      case ToggledFeature.NEW_TRANSACTIONS_MANAGEMENT:
        return true;
      default:
        return false;
    }
  };

  return {
    isNewUiEnabled,
    isFeatureEnabled,
    isTeamMembersManagementEnabled,
    isTeamMembersManagementSeatsEnabled,
    isTasksGroupsEnabled,
    isLaunchingFromContractEnabled,
    isDetailsFieldSetsEnabled,
    isNewSmartTagsEnabled,
    isEventTasksEnabled,
    isPlanEventTasksEnabled,
    isSendDocumentsAsAttachmentsEnabled,
    isNewPriceEnabled,
    isNewTransactionsManagementEnabled,
    isInDevelopment,
    isRequestMarketsEnabled,
    isCustomMarketsEnabled,
    isSetupAreasVersioningEnabled,
    isCreateNoteFromTransactionPageEnabled,
    isMarketTimezoneEnabled,
    isClientPortalBrandingEnabled,
    isPreferencesPageEnabled,
    isBulkContactsImportEnabled,
    isClientAppEnabled,
    isHtmlSignatureEnabled,
    isScratchpadEnabled,
    isOutlookIntegrationEnabled,
    isLlmChatEnabled,
    isNewWysiwygStylesEnabled,
    isNewWysiwygStylesConfigurationEnabled,
    isLockInBannerEnabled,
    isAutoAssignRolesEnabled,
    isMultiWorkflowEnabled,
    isEmailDescriptionToolEnabled,
    isApplyAdditionalWorkflowEnabled,
  };
}

export function withToggleWrapper<T>(
  WrappedComponent: React.ComponentType<T>,
  feature: ToggledFeature
) {
  return (props: T) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    const component = <WrappedComponent {...props} />;

    return (
      <ToggleWrapper feature={feature}>
        {component}
      </ToggleWrapper>
    );
  };
}
